<script setup lang="ts">
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const authStore = useAuthStore()
const config = useRuntimeConfig()
const { nativeShare } = useNativeClientService()
const { trackEvent } = useAnalyticsTrackingService()

const inviteDays = 30

const state = reactive({
  showingSuccessIndicator: false
})

const inviteLink = computed(() => {
  const userId = authStore.currentUser?.id
  const baseUrl = config.public.inviteShareUrl as string | undefined

  if (!userId || !baseUrl) return null

  try {
    const url = new URL(baseUrl)
    url.searchParams.set('referrer', String(userId))
    return url.toString()
  } catch (err) {
    console.error('Invalid INVITE_SHARE_URL:', err)
    return null
  }
})

const dayLabel = computed(() => t('referral_day_label').replace('%d', String(inviteDays)))

const flashCopiedIndicator = () => {
  state.showingSuccessIndicator = true

  setTimeout(() => {
    state.showingSuccessIndicator = false
  }, 2000)
}

const copyLink = async () => {
  if (!inviteLink.value) return

  try {
    await navigator.clipboard.writeText(inviteLink.value)
    flashCopiedIndicator()
  } catch (err) {
    console.error(err)
  }
}

const onCopyClick = () => {
  trackEvent(ANALYTICS_EVENTS.referralCopyCode())
  copyLink()
}

const shareLink = async () => {
  if (!inviteLink.value) return

  trackEvent(ANALYTICS_EVENTS.referralShareCode())

  const shared = nativeShare(
    t('referral_invite_link_clip_label'),
    t('referral_invite_description'),
    inviteLink.value
  )

  if (!shared) {
    await copyLink()
  }
}

onMounted(() => {
  if (inviteLink.value) {
    trackEvent(ANALYTICS_EVENTS.referralShow())
  }
})
</script>

<template>
  <Modal
    v-if="inviteLink"
    hide-header
    max-width="780px"
    max-height="640px"
    modal-classes="!tw-bg-black"
    body-classes="!tw-p-0 tw-bg-black tw-text-white"
    data-testid="invite-friend-modal"
    @close="emit('close')"
  >
    <template #body>
      <div
        class="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-bg-black md:tw-flex-row"
        style="min-height: 480px"
      >
        <IconsCardoRideChevron
          class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-1/2 tw-h-full tw-w-auto -tw-translate-x-1/2 tw-text-white/[0.08] md:tw-translate-x-0 md:tw-scale-150"
        />

        <button
          type="button"
          class="tw-absolute tw-right-4 tw-top-4 tw-z-20 tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white/15 tw-text-white tw-backdrop-blur tw-transition-colors hover:tw-bg-white/25"
          :aria-label="t('referral_back_content_description')"
          data-testid="invite-friend-modal-close"
          @click="emit('close')"
        >
          <FontAwesomeIcon icon="xmark" />
        </button>

        <div
          class="tw-relative tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center tw-px-6 tw-pb-2 tw-pt-10 md:tw-w-1/2 md:tw-px-12 md:tw-py-10"
        >
          <img
            src="/images/referral/guestpass.png"
            alt=""
            aria-hidden="true"
            class="tw-block tw-w-full tw-max-w-xs md:tw-max-w-none"
          />
        </div>

        <div
          class="tw-relative tw-z-10 tw-flex tw-flex-1 tw-flex-col tw-px-6 tw-pb-8 tw-pt-2 md:tw-px-10 md:tw-py-10"
        >
          <h2 class="tw-mb-6 tw-text-center tw-text-lg tw-font-semibold md:tw-mb-8 md:tw-text-left">
            {{ t('referral_invite_title') }}
          </h2>

          <h3
            class="tw-mb-3 tw-text-center tw-text-2xl tw-font-bold tw-leading-tight md:tw-text-left md:tw-text-3xl"
          >
            {{ t('referral_headline_prefix') }}
            <span class="tw-text-riser-blue">{{ dayLabel }}</span>
            {{ t('referral_headline_suffix') }}
          </h3>

          <p
            class="tw-mb-6 tw-text-center tw-text-sm tw-text-white/70 md:tw-text-left md:tw-text-base"
          >
            {{ t('referral_invite_description') }}
          </p>

          <div class="tw-mt-auto tw-flex tw-flex-col tw-items-center md:tw-items-stretch">
            <div class="tw-relative tw-w-full">
              <InputAppButton
                class="tw-w-full tw-rounded-full tw-bg-riser-blue tw-px-6 tw-py-3 tw-text-center tw-text-base tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-riser-blue/90"
                data-testid="invite-friend-modal-share"
                @click="shareLink"
              >
                {{ t('referral_share_invite_link') }}
              </InputAppButton>

              <div
                v-if="state.showingSuccessIndicator"
                class="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-black/70 tw-text-sm tw-text-white tw-backdrop-blur"
              >
                {{ t('referral_link_copied') }}
              </div>
            </div>

            <InputAppButton
              class="tw-mt-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-sm tw-text-white/80 hover:tw-text-white"
              data-testid="invite-friend-modal-copy"
              @click="onCopyClick"
            >
              <FontAwesomeIcon icon="copy" class="tw-text-xs" />
              {{ t('referral_copy_link') }}
            </InputAppButton>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>
